// Lógica pura del detalle de pedido: qué pasó con el pedido, con qué evidencia y
// qué puede hacer el operador al respecto. Sin React ni red, para poder probar
// cada estado sin montar la vista.
//
// La "franja de resolución" del detalle responde UNA pregunta distinta según el
// estado (¿se entregó y lo puedo probar? ¿por qué falló? ¿quién lo tiene?), y
// `buildResolution` arma exactamente lo que esa pregunta necesita.

import { haversineKm } from "../../views/adminView/adminRoute/rutificador/rutificadorUtils";

// Sobre esta distancia entre el punto del evento (entrega o intento) y la
// dirección, la franja marca "lejos". Decisión de producto (2026-10-03): el GPS
// de un teléfono en ciudad se equivoca decenas de metros y un edificio grande
// ocupa una manzana; 300 m deja afuera ese ruido y sigue atrapando la entrega
// marcada desde otra cuadra.
export const FAR_FROM_ADDRESS_METERS = 300;

// Menos dígitos que esto no es un número al que se pueda llamar. Cubre el
// "XXXXXXX" con que MercadoLibre oculta el teléfono del comprador, que hasta
// ahora se pintaba como enlace `tel:`.
export const MIN_CALLABLE_PHONE_DIGITS = 8;

// La coordenada de la dirección sólo es confiable si el geocoder encontró calle
// Y número ('ok') o si una persona la resolvió a mano ('resolved'): en esos dos
// casos, y sólo en esos, microservice-packages la persiste. Sin el bloque
// `geocoding` (pedidos anteriores) no se sabe de dónde salió, y una distancia
// contra una coordenada dudosa marcaría "lejos" entregas correctas.
const VERIFIED_GEOCODING = new Set(["ok", "resolved"]);

// Centinelas que mandan la app y las integraciones en lugar de un dato real:
// 'N/D' cuando el operador configuró no pedir ese dato, 'N/A' en algunos
// pedidos de MercadoLibre. Mostrarlos haría parecer que alguien escribió basura.
const PLACEHOLDER_VALUES = new Set(["N/D", "N/A"]);

export const failedOptionMap = {
  direccion_incorrecta: "Dirección incorrecta",
  direccion_inaccesible: "Dirección inaccesible",
  sin_moradores: "Sin moradores",
  cliente_rechaza: "El cliente rechaza o desconoce la compra",
  // Sólo lo ofrece la app cuando el pedido es contra entrega: en COD, si el
  // cliente no paga, el repartidor no entrega y el intento se marca fallido.
  // El catálogo de motivos está hard-codeado en la app
  // (app/deliver-failed/[id].tsx, `failureOptions`), NO es configurable por
  // operador, así que este mapa hay que mantenerlo a mano en paralelo.
  no_pago: "No pagó el monto a cobrar",
};

// Motivos de devolución. Catálogo canónico: enum `ReturnReason` en
// microservice-packages/src/api/packages/dto/update-return-info.dto.ts (lo
// elige el repartidor en app/returns/[id].tsx). Las etiquetas son las de la app.
export const returnReasonMap = {
  seller_request: "Retiro solicitado por el vendedor",
  product_change: "Cambio de producto",
  customer_rejected: "Cliente final rechazó la entrega",
};

// ── Pago contra entrega (COD) ────────────────────────────────────────────────
// Catálogo canónico: microservice-packages/src/api/packages/constants/
// cash-on-delivery.constants.ts. Acá se replican SOLO las etiquetas visibles;
// si el backend agrega un medio nuevo, el valor crudo se muestra tal cual en vez
// de desaparecer de la vista.
export const codStatusMap = {
  pending: { label: "Por cobrar", pill: "warning" },
  collected: { label: "Cobrado", pill: "success" },
  cancelled: { label: "Sin cobro (anulado)", pill: "neutral" },
};

export const codMethodMap = {
  cash: "Efectivo",
  transfer_driver: "Transferencia al repartidor",
  transfer_seller: "Transferencia al vendedor",
};

// ── Valores ──────────────────────────────────────────────────────────────────

/** Valor de evidencia realmente capturado, o '' si es vacío o un centinela. */
export function cleanEvidenceValue(value) {
  const text = typeof value === "string" ? value.trim() : "";
  return text && !PLACEHOLDER_VALUES.has(text.toUpperCase()) ? text : "";
}

/** Nombre y apellido sin centinelas ('Juan', 'N/A' → 'Juan'). */
export function personName(first, last) {
  return [cleanEvidenceValue(first), cleanEvidenceValue(last)].filter(Boolean).join(" ");
}

export function isCallablePhone(raw) {
  return String(raw ?? "").replace(/\D/g, "").length >= MIN_CALLABLE_PHONE_DIGITS;
}

export const hasCoords = (c) => c != null && c.latitude != null && c.longitude != null;

export function getUserDisplayName(entry) {
  if (entry.updatedByUser) {
    return entry.updatedByUser;
  }
  if (entry.updatedBy && typeof entry.updatedBy === "object") {
    const name = entry.updatedBy.name || "";
    const lastName = entry.updatedBy.lastName || "";
    if (lastName && lastName.trim()) {
      return `${name} ${lastName}`.trim();
    } else if (name && name.trim()) {
      return name.trim();
    }
  }
  return null;
}

/** Tono del puntaje de calidad de la IA: mismos umbrales en todo el detalle. */
export function qualityTone(score) {
  if (score < 70) return "error";
  if (score < 85) return "warning";
  return "success";
}

// ── Distancia a la dirección ─────────────────────────────────────────────────

export function verifiedDestination(pkg) {
  if (!VERIFIED_GEOCODING.has(pkg?.geocoding?.status)) return null;
  const sa = pkg.shipping_address;
  if (sa?.latitude == null || sa?.longitude == null) return null;
  const latitude = Number(sa.latitude);
  const longitude = Number(sa.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

/** { meters, far } entre `coords` y la dirección verificada, o null si no se puede afirmar. */
export function distanceFromAddress(pkg, coords) {
  const destination = verifiedDestination(pkg);
  if (!destination || !hasCoords(coords)) return null;
  const lat = Number(coords.latitude);
  const lng = Number(coords.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const meters = haversineKm({ lat, lng }, { lat: destination.latitude, lng: destination.longitude }) * 1000;
  return { meters, far: meters > FAR_FROM_ADDRESS_METERS };
}

/** "35 m" / "320 m" / "1,2 km" (separador decimal del idioma activo). */
export function formatDistance(meters, locale) {
  const rounded = meters < 100 ? Math.round(meters) : Math.round(meters / 10) * 10;
  if (rounded < 1000) return `${rounded} m`;
  const km = (meters / 1000).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return `${km} km`;
}

// ── Bitácora y evidencia por evento ──────────────────────────────────────────

/** Historial de estados, el más reciente primero. */
export function sortStatusHistory(history) {
  return [...(history || [])].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
}

const entryKeyOf = (entry) => entry._id || entry;

/**
 * Cada entrada fallida (6/7) del historial → su índice entre las fallidas, del
 * más reciente (0) al más antiguo. Con eso se cruza con `failedAttempts`.
 */
export function failedIndexByEntry(sortedHistory) {
  const indices = new Map();
  let failedCounter = 0;
  sortedHistory.forEach((entry) => {
    if (entry.status === 6 || entry.status === 7) {
      indices.set(entryKeyOf(entry), failedCounter);
      failedCounter++;
    }
  });
  return indices;
}

const pickEvidence = (source) => ({
  images: source?.images,
  coords: source?.coords,
  option: source?.option,
  description: source?.description,
  selectedOptions: source?.selectedOptions,
});

const NO_EVIDENCE = { images: null, coords: null, option: null };

function failedAttemptEvidence(pkg, failedIndex) {
  const attempts = pkg?.failedAttempts;
  if (attempts?.length) {
    const attempt = attempts[attempts.length - 1 - failedIndex];
    if (attempt) return pickEvidence(attempt);
  }
  return pickEvidence(pkg?.failed);
}

/** Imágenes y coordenadas asociadas a un estado del historial. */
export function evidenceForStatus(pkg, status, failedIndex = 0) {
  if (!pkg) return NO_EVIDENCE;
  switch (status) {
    case 4: // Entregado
      return { images: pkg.images, coords: pkg.coords, option: null };
    case 22: // Devolución en bodega
      return {
        images: pkg.returnInfo?.warehouseImages || pkg.returnInfo?.images,
        coords: null,
        option: null,
      };
    case 6: // Fallido
    case 7:
      return failedAttemptEvidence(pkg, failedIndex);
    case 41: // Devuelto (completado)
      return {
        images: pkg.returnInfo?.finalReturnImages || pkg.images,
        coords: pkg.returnInfo?.finalReturnCoords || pkg.coords,
        option: null,
      };
    default: // incluye 31, En ruta (devolución): sin evidencia propia
      return NO_EVIDENCE;
  }
}

// ── Mapa del pedido ──────────────────────────────────────────────────────────

const toCoords = (c) => {
  if (!hasCoords(c)) return null;
  const latitude = Number(c.latitude);
  const longitude = Number(c.longitude);
  return Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : null;
};

/**
 * Todo lo que el mapa del detalle dibuja: la dirección de entrega, cada intento
 * fallido (numerado en orden cronológico), la entrega y la devolución completada.
 *
 * La dirección se dibuja aunque su ubicación no esté verificada (pedidos de
 * MercadoLibre sin bloque `geocoding`): es el punto contra el que se compara el
 * resto. `verified` lo dice, y la distancia sigue saliendo sólo contra una
 * dirección verificada, igual que en la franja.
 *
 * Cada punto de evento lleva la `key` de su entrada del historial, la misma que
 * usan la bitácora y la franja para pedir "mostrar este punto".
 */
export function mapPoints(pkg, sortedHistory) {
  if (!pkg) return [];
  const points = [];

  const sa = pkg.shipping_address;
  const addressCoords = toCoords(sa);
  if (addressCoords) {
    points.push({
      key: "address",
      kind: "address",
      coords: addressCoords,
      verified: verifiedDestination(pkg) != null,
    });
  }

  const indices = failedIndexByEntry(sortedHistory);
  const totalFailed = indices.size;
  // Del más antiguo al más reciente: así la leyenda se lee como pasó.
  sortedHistory
    .filter((entry) => entry.status === 6 || entry.status === 7)
    .reverse()
    .forEach((entry) => {
      const key = entryKeyOf(entry);
      const failedIndex = indices.get(key) ?? 0;
      const ev = evidenceForStatus(pkg, entry.status, failedIndex);
      const coords = toCoords(ev.coords);
      if (!coords) return;
      points.push({
        key,
        kind: "failed",
        coords,
        at: entry.updatedAt,
        attempt: totalFailed - failedIndex,
        reason: ev.option ? failedOptionMap[ev.option] || ev.option : null,
        distance: distanceFromAddress(pkg, coords),
      });
    });

  // Entrega y devolución tienen UNA coordenada en el paquete: se ubican en el
  // evento más reciente de ese estado, no en cada repetición del historial.
  const finalEvents = [
    { status: 4, kind: "delivered", coords: pkg.coords },
    { status: 41, kind: "returned", coords: pkg.returnInfo?.finalReturnCoords },
  ];
  finalEvents.forEach(({ status, kind, coords: raw }) => {
    const entry = sortedHistory.find((e) => e.status === status);
    const coords = toCoords(raw);
    if (!entry || !coords) return;
    points.push({
      key: entryKeyOf(entry),
      kind,
      coords,
      at: entry.updatedAt,
      distance: distanceFromAddress(pkg, coords),
    });
  });

  return points;
}

// ── Franja de resolución ─────────────────────────────────────────────────────

export function resolutionKind(status) {
  switch (status) {
    case 4:
      return "delivered";
    case 6:
    case 7:
      return "failed";
    case 3:
    case 30:
      return "in_route";
    case 1:
    case 2:
      return "warehouse";
    case 0:
    case 100:
      return "pending";
    case 5:
      return "cancelled";
    case 21:
    case 22:
    case 31:
    case 41:
      return "return";
    default:
      return "other";
  }
}

function codSummary(pkg) {
  const cod = pkg?.cashOnDelivery;
  const amount = Number(cod?.amount);
  // "Este pedido se cobra" = monto > 0, igual que la sección de cobro: no hay un
  // flag de habilitado en el paquete.
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const collected =
    cod.collectedAmount != null && cod.collectedAmount !== "" ? Number(cod.collectedAmount) : null;
  return {
    status: cod.status || null,
    amount,
    collectedAmount: Number.isFinite(collected) ? collected : null,
    currency: cod.currency,
    method: cod.method || null,
  };
}

/**
 * Todo lo que la franja muestra para el estado ACTUAL del pedido.
 *
 * `entryKey` identifica el evento del historial que la franja ya cuenta, para
 * que la bitácora no repita sus fotos, su receptor ni su ubicación.
 */
export function buildResolution(pkg, sortedHistory) {
  const kind = resolutionKind(pkg.status);
  // El historial viene del más reciente al más antiguo: el primero de este tipo
  // es el que dejó al pedido en su estado actual.
  const entry = sortedHistory.find((e) => resolutionKind(e.status) === kind) || null;
  const entryKey = entry ? entryKeyOf(entry) : null;

  const resolution = {
    kind,
    status: pkg.status,
    entryKey,
    at: entry?.updatedAt || (kind === "pending" ? pkg.createdAt : null) || null,
    by: entry ? getUserDisplayName(entry) : null,
    photos: [],
    receiver: null,
    attempt: null,
    failure: null,
    eventCoords: null,
    distance: null,
    returnReason: null,
    cod: codSummary(pkg),
  };

  if (kind === "delivered") {
    resolution.photos = pkg.images || [];
    resolution.receiver = {
      name: personName(pkg.realReceiver?.name, pkg.realReceiver?.lastName),
      doc: cleanEvidenceValue(pkg.realReceiver?.rut),
    };
    resolution.eventCoords = hasCoords(pkg.coords) ? pkg.coords : null;
  } else if (kind === "failed") {
    const indices = failedIndexByEntry(sortedHistory);
    const ev = evidenceForStatus(pkg, entry?.status ?? pkg.status, indices.get(entryKey) ?? 0);
    resolution.photos = ev.images || [];
    resolution.attempt = indices.size || pkg.failedAttempts?.length || null;
    resolution.failure = {
      reason: ev.option ? failedOptionMap[ev.option] || ev.option : null,
      options: ev.selectedOptions || [],
      note: ev.description || "",
    };
    resolution.eventCoords = hasCoords(ev.coords) ? ev.coords : null;
  } else if (kind === "return") {
    resolution.photos = evidenceForStatus(pkg, pkg.status).images || [];
    const reason = pkg.returnInfo?.returnReason;
    resolution.returnReason = reason ? returnReasonMap[reason] || reason : null;
  }

  resolution.distance = resolution.eventCoords
    ? distanceFromAddress(pkg, resolution.eventCoords)
    : null;
  return resolution;
}

// ── Dónde va cada acción ─────────────────────────────────────────────────────
//
// La franja lleva las acciones que resuelven la pregunta de ESE estado, la
// primera como principal. El encabezado lleva el resto. Cada acción aparece una
// sola vez: si la franja la usa, el encabezado no la repite.

const BAND_ACTIONS = {
  delivered: ["comprobante", "evaluacion"],
  failed: ["llamar", "editar", "contactar"],
  in_route: ["contactar", "llamar"],
  warehouse: ["etiqueta"],
  pending: ["etiqueta", "cancelar"],
};

// El comprobante también va acá: un pedido entregado y después devuelto sigue
// teniendo prueba de entrega, aunque la franja ya hable de la devolución.
const HEADER_ACTIONS = ["comprobante", "etiqueta", "etiquetaParis", "editar", "cancelar", "auditoria"];

/** `available`: Set con las acciones que el usuario puede usar sobre este pedido. */
export function planActions(kind, available) {
  const band = (BAND_ACTIONS[kind] || []).filter((id) => available.has(id));
  const header = HEADER_ACTIONS.filter((id) => available.has(id) && !band.includes(id));
  return { band, header };
}
